<script lang="ts" setup>
import { composerMinMaxDates } from '~/queries/composer'
import { workMinMaxLength } from '~/queries/work'
import { workMinMaxDate, getStaffSearch } from '~/queries/work'
import { getCountries } from '~/queries/countries'
import { getThesaurusList } from '~/queries/thesaurus'
import { getGenreList } from '~/queries/genre'
import AdvancedSearchGenerator from '~/components/form/AdvancedSearchGenerator.vue'
import { ref } from 'vue'
import { useI18n } from '#i18n'

// General Advanced Search
const { t, locale } = useI18n()
const router = useRouter()
const resultList = ref(null)
const route = useRoute()
const instrumentsLoading = ref(false)
const genreLoading = ref(false)
let searchData = ref(null)
let searchPending = ref(false)
let searchError = ref(null)

// Country and Date data
const countries = ref(null)
const birthDateMin = ref(null)
const birthDateMax = ref(null)
const deathDateMin = ref(null)
const deathDateMax = ref(null)

const fetchCountryAndDateData = async () => {
  const countryData = await useAsyncData('countries', () =>
    execQuery(getCountries)
  )
  countries.value = countryData.data?.value?.data?.countryList

  const { data } = await useAsyncData('dates', () =>
    execQuery(composerMinMaxDates)
  )
  const personDates = data.value.data.personMinMaxDate
  birthDateMin.value = personDates.birthDate.min.slice(0, 4)
  birthDateMax.value = personDates.birthDate.max.slice(0, 4)
  deathDateMin.value = personDates.deathDate.min.slice(0, 4)
  deathDateMax.value = personDates.deathDate.max.slice(0, 4)
}

// Fetch Data for Work Fields
const durationMin = ref(null)
const durationMax = ref(null)
const compositionMin = ref(null)
const compositionMax = ref(null)
const creationMin = ref(null)
const creationMax = ref(null)
const instrumentsList = ref(null)
const genresList = ref(null)
const thesauri = ref(null)

const fetchWorkData = async () => {
  genreLoading.value = true
  instrumentsLoading.value = true
  const workMinMax = await useAsyncData('workMinMaxLength', () =>
    execQuery(workMinMaxLength)
  )
  durationMin.value = workMinMax.data.value.data.workMinMaxLength.min
  durationMax.value = workMinMax.data.value.data.workMinMaxLength.max

  const minMaxWorkDate = await useAsyncData('minMaxWorkDate', () =>
    execQuery(workMinMaxDate)
  )
  if (minMaxWorkDate.data.value) {
    compositionMin.value =
      minMaxWorkDate.data.value.data.workMinMaxDate.compositionDate.min.slice(
        0,
        4
      )
    compositionMax.value =
      minMaxWorkDate.data.value.data.workMinMaxDate.compositionDate.max.slice(
        0,
        4
      )
    creationMin.value =
      minMaxWorkDate.data.value.data.workMinMaxDate.creationDate.min.slice(0, 4)
    creationMax.value =
      minMaxWorkDate.data.value.data.workMinMaxDate.creationDate.max.slice(0, 4)
  }

  const searchStaff = await useAsyncData('data', () =>
    execQuery(getStaffSearch, {
      ...route.query,
      ...(route.query.electronic || route.query.electronic === 'unknown'
        ? route.query.electronic === 'true'
          ? { electronic: true }
          : { electronic: false }
        : {}),
    })
  )
  thesauri.value = searchStaff.data.value.data.staffSearchData.thesauri

  useAsyncData('thesaurusList', () =>
    execQuery(getThesaurusList, {
      lang: locale.value,
      role: 'instrument',
    })
  ).then((thesaurusList) => {
    instrumentsList.value = thesaurusList.data.value.data.values?.find(({ nameFr }) => nameFr === 'choix')?.children ?? []
    instrumentsLoading.value = false
  })

  useAsyncData('genresList', () =>
    execQuery(getGenreList, {
      lang: locale.value,
    })
  ).then((genres) => {
    genresList.value = genres.data.value.data.values
    genreLoading.value = false
  })
}

await Promise.all([fetchCountryAndDateData(), fetchWorkData()])

const { composerFields, floraFields, workFields } = getAdvancedSearchFields(
  t,
  locale,
  { countries, birthDateMin, birthDateMax, deathDateMin, deathDateMax },
  {
    thesauri,
    durationMin,
    durationMax,
    creationMin,
    creationMax,
    compositionMin,
    compositionMax,
  }
)

const searchCount = computed<Collection[]>(() => {
  if (searchData.value === null || searchPending.value || searchError.value)
    return null
  return searchData.value.count
})
const searchResults = computed(() => {
  if (searchData.value === null || searchPending.value || searchError.value)
    return []
  const results = []
  for (const document of searchData.value.documents) {
    results.push({
      title: document.document.title,
      titleHtml: document.document.titleHtml,
      type: document.document.type,
      slug: document.document.slug,
      subtitle: document.document.description ?? document.document.summary,
      subtitleEn: document.document.descriptionEn ?? document.document.summary,
      excerpt: document.document.composers,
      page: 'advanced_search',
    })
  }
  return results
})

const advancedSearchValues = getAdvancedSearchValues()

let composerValues = reactive(advancedSearchValues.initComposerValues)
let workValues = reactive(advancedSearchValues.initWorkValues)
let floraValues = reactive(advancedSearchValues.initFloraValues)

const _formValues = ref({})
const _tableName = ref('')
const currentPage = ref(1)

const includes = {
  works: [
    'title',
    'description',
    'descriptionEn',
    'type',
    'slug',
    'composers',
    'compositionYear',
  ],
  persons: ['title', 'type', 'slug', 'description'],
  unimarcs: ['title', 'titleHtml', 'type', 'slug', 'summary'],
}

onMounted(async () => {
  const { table, p } = route.query
  let { values } = route.query
  if (table && values) {
    try {
      values = JSON.parse(values)
      await advancedSearch(values, p, table)
      switch (table) {
        case 'works':
          Object.assign(workValues, values)
          break
        case 'persons':
          Object.assign(composerValues, values)
          break
        case 'unimarcs':
          Object.assign(floraValues, values)
          break
      }
    } catch (err) {
      console.log(err)
      //
    }
  }
})

const SORTBY = {
  works: 'title:asc',
  persons: 'lastName:asc',
}

async function advancedSearch(formValues, currentPage, tableName) {
  _formValues.value = formValues
  _tableName.value = tableName

  const filters = await buildFilters(_formValues.value, genresList.value)

  const _query = {
    q: '*',
    p: currentPage,
    filter_by: filters,
    include: includes[tableName].join('-'),
    limit: 10,
    sort_by: SORTBY[tableName],
  }

  await router.push({
    query: {
      table: tableName,
      values: JSON.stringify(formValues),
      p: currentPage,
    },
  })
  const result = await useAsyncData(tableName, () =>
    execAdvancedSearch(tableName, _query)
  )
  searchData.value = result.data.value
  resultList.value.scrollIntoView({ behavior: 'smooth' })
}

async function updateUrl() {
  await advancedSearch(_formValues.value, currentPage.value, _tableName.value)
}

async function clearSearch(type: string) {
  await router.push({
    query: {},
  })
  const { initWorkValues, initFloraValues, initComposerValues } =
    getAdvancedSearchValues()
  switch (type) {
    case 'persons':
      Object.assign(composerValues, initComposerValues)
      _formValues.value = initComposerValues
      break
    case 'works':
      Object.assign(workValues, initWorkValues)
      _formValues.value = initWorkValues
      break
    case 'unimarcs':
      Object.assign(floraValues, initFloraValues)
      _formValues.value = initFloraValues
      break
  }
}
</script>
<template>
  <div>
    <PatternHero size="small" color-class="text-primary-400">
      <div class="p-3 lg:p-6">
        <h2 class="text-xl font-semibold first-letter:uppercase">
          {{ $t('labels.advanced_search') }}
        </h2>
      </div>
    </PatternHero>
    <div class="mx-auto max-w-7xl px-6 pt-6 md:pt-10 xl:px-0">
      <DataCollapse class="my-4" :default-open="route.query.table === 'persons'">
        <template #title>{{ $t('labels.composer', 2) }}</template>
        <AdvancedSearchGenerator :values.sync="composerValues" :fields="composerFields" @advanced-search="
          () => {
            currentPage = 1
            advancedSearch(composerValues, currentPage, 'persons')
          }
        " @clear-search="clearSearch('persons')" />
      </DataCollapse>
      <DataCollapse class="my-8" :default-open="route.query.table === 'works'">
        <template #title>{{ $t('labels.work', 2) }}</template>
        <AdvancedSearchGenerator :values.sync="workValues" :fields="workFields" :genres-list="genresList"
          :genre-loading="genreLoading" :instruments-list="instrumentsList" :instruments-loading="instrumentsLoading"
          @advanced-search="
            () => {
              currentPage = 1
              advancedSearch(workValues, currentPage, 'works')
            }
          " @clear-search="clearSearch('works')" />
      </DataCollapse>
      <DataCollapse class="my-8" :default-open="route.query.table === 'unimarcs'">
        <template #title>{{ $t('labels.physical_objects') }}</template>
        <AdvancedSearchGenerator :values.sync="floraValues" :fields="floraFields" @advanced-search="
          () => {
            currentPage = 1
            advancedSearch(floraValues, currentPage, 'unimarcs')
          }
        " @clear-search="clearSearch('unimarcs')" />
      </DataCollapse>
      <!-- Resource results -->

      <div class="-mt-10" ref="resultList" />
      <div class="mt-20">
        <hr />
        <h2 class="flex gap-2 items-center mt-6">
          <span class="text-mobile-2xl font-semibold md:text-2xl first-letter:uppercase">{{ $t('labels.result')
          }}</span>
          <span v-if="searchCount" class="text-mobile-lg md:text-lg text-shade-500">({{ searchCount }})</span>
        </h2>
        <div v-if="searchData" class="mx-auto flex max-w-7xl gap-7 lg:gap-14">
          <div class="hidden md:block lg:shrink-0">
            <p></p>
          </div>
          <div class="pt-6 md:pt-0">
            <p class="text-mobile-lg text-shade-500 md:hidden">
              {{ $t('search.total_result', searchCount) }}
            </p>
            <div class="divide-y divide-shade-200">
              <ListResource v-for="(result, i) in searchResults" :key="i" :resource="result"
                class="py-4 first:mt-4 md:p-4 md:first:mt-0" />
            </div>
            <div class="mt-4 flex justify-center md:mt-10 md:justify-start">
              <DataPagination v-model="currentPage" :count="searchCount" :page-size="10"
                @update:modelValue="updateUrl()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
